import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useApiProblem } from '@/shared/api';
import { SurfaceLoadError, SurfaceLoading, SurfaceStaleHint } from '@/shared/components';
import { resourceLoadFailureText } from '../resourceProblemText';

/**
 * Ladezustände der Ressourcenbelegung: lädt, offline ohne Daten („Keine Verbindung“, nie „keine
 * Ressourcen“), Ladefehler mit Grund und „Erneut laden“, veraltet nach misslungenem Nachladen.
 */
export function ResourceLoadState({
  query,
  children,
}: {
  query: Pick<
    UseQueryResult<unknown>,
    | 'data'
    | 'error'
    | 'isError'
    | 'isPaused'
    | 'isPending'
    | 'isFetching'
    | 'fetchStatus'
    | 'refetch'
  >;
  children: ReactNode;
}) {
  const problem = useApiProblem(query.error ?? null);
  const retry = () => void query.refetch();
  if (!query.data && query.fetchStatus === 'paused') {
    return (
      <SurfaceLoadError
        message="Keine Verbindung – die Ressourcenbelegung ist nicht geladen."
        onRetry={retry}
        retrying={query.isFetching}
      />
    );
  }
  if (!query.data && query.isError) {
    return (
      <SurfaceLoadError
        message={`Die Ressourcenbelegung konnte nicht geladen werden. ${problem ? resourceLoadFailureText(problem) : ''}`.trim()}
        onRetry={retry}
        retrying={query.isFetching}
      />
    );
  }
  if (query.isPending) return <SurfaceLoading label="Ressourcen werden geladen" rows={3} />;
  return (
    <>
      {(query.isError || query.isPaused) && (
        <SurfaceStaleHint onRetry={retry} retrying={query.isFetching} />
      )}
      {children}
    </>
  );
}
